import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContentBlock from '@/components/molecules/ContentBlock';
import { Disclosure } from '@/components/molecules/Disclosure';
import EmptyState from '@/components/molecules/EmptyState';
import type { ClientFishGroupsOverview } from '@/domain/fishGroups';

import CreateFishGroupModal from './actions/CreateFishGroupModal';
import { CageTimelineModal } from './CageTimelineModal';
import { FishGroupDetailModal } from './FishGroupDetailModal';
import LocationFishGroupsTable from './LocationFishGroupsTable';

import styles from './FishGroupsOverview.module.scss';

interface Props {
    overview: ClientFishGroupsOverview;
}

interface CreateTarget {
    id: number;
    name: string;
    locationName: string;
}

interface TimelineTarget {
    id: number;
    name: string;
}

export default function FishGroupsOverview(props: Props) {
    const { overview } = props;
    const { t } = useTranslation('fishGroups');
    const [openFishGroupId, setOpenFishGroupId] = useState<number | null>(null);
    const [createTarget, setCreateTarget] = useState<CreateTarget | null>(null);
    const [timelineTarget, setTimelineTarget] = useState<TimelineTarget | null>(null);

    const renderLocations = (locations: ClientFishGroupsOverview['activeLocations']) =>
        locations.map((location) => (
            <LocationFishGroupsTable
                key={location.locationId}
                location={location}
                onOpenFishGroup={setOpenFishGroupId}
                onCreateFishGroup={setCreateTarget}
                onOpenTimeline={setTimelineTarget}
            />
        ));

    const activeContent =
        overview.activeLocations.length === 0 ? (
            <EmptyState title={t('overview.noActiveLocations')} appearance="quiet" />
        ) : (
            <div className={styles.collapsableBody}>
                {renderLocations(overview.activeLocations)}
            </div>
        );

    const inactiveContent =
        overview.inactiveLocations.length === 0 ? (
            <EmptyState title={t('overview.noInactiveLocations')} appearance="quiet" />
        ) : (
            <div className={styles.collapsableBody}>
                {renderLocations(overview.inactiveLocations)}
            </div>
        );

    return (
        <>
            <ContentBlock title={t('overview.title')}>
                <div className={styles.sections}>
                    <Disclosure title={t('overview.activeLocations')} defaultOpen spacing="none">
                        {activeContent}
                    </Disclosure>
                    <Disclosure title={t('overview.inactiveLocations')} spacing="none">
                        {inactiveContent}
                    </Disclosure>
                </div>
            </ContentBlock>
            <FishGroupDetailModal
                clientId={overview.clientId}
                fishGroupId={openFishGroupId}
                isOpen={openFishGroupId !== null}
                onClose={() => setOpenFishGroupId(null)}
            />
            <CreateFishGroupModal
                clientId={overview.clientId}
                cage={createTarget}
                isOpen={createTarget !== null}
                onClose={() => setCreateTarget(null)}
            />
            <CageTimelineModal
                clientId={overview.clientId}
                cage={timelineTarget}
                isOpen={timelineTarget !== null}
                onClose={() => setTimelineTarget(null)}
                onOpenFishGroup={(fishGroupId) => {
                    // Drill from the cage timeline into the FG detail in one
                    // step: close the timeline and open the detail modal.
                    // Avoids stacked modals and keeps a single "active" view.
                    setTimelineTarget(null);
                    setOpenFishGroupId(fishGroupId);
                }}
            />
        </>
    );
}
